// Work orders (Session ATZ, email system Phase 2, Item 3; Director decision DE-4 as recommended: propose the call-out or
// job for one click).
//
// A managing agent's property platform (today Dwellant, for Haus Block Management's buildings) emails ELE whenever it
// assigns ELE a request. The email engine reads each one and PROPOSES it here as a work order, matched to the site by the
// platform's own name for the building (or one of the site's aliases), and files the email: it needs no reply. An update
// about the same request number attaches to the same work order.
//
// THE ONE CLICK. "Raise call-out" and "Raise repair" go through the SAME routes the office's own forms use (POST
// /api/callouts and POST /api/schedule/planned-repair), given this work order's id, so a call-out raised here is exactly a
// call-out raised anywhere else (its Xero draft included). The server claims the work order first, so two people pressing
// at once raise it once. A work order the platform could not place waits for a person to choose its site; ticking
// "remember" saves the platform's name on the site, so its next request is matched by itself.
//
// RULE 1: a pending or dismissed work order can be deleted (its emails stay on record); one that work was raised from is
// kept as the record of where that work came from.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { SitePrefill, WorkOrder, WorkOrderDetail, WorkOrderStatusFilter } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";

const FILTERS: { value: WorkOrderStatusFilter; label: string }[] = [
  { value: "pending", label: "Waiting" },
  { value: "accepted", label: "Raised" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

const LINK_BTN = "font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong disabled:opacity-50";
const FIELD = "rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";

function when(iso: string | null): string {
  return iso ? formatDateTime(iso) : "";
}

/** The default description for the work raised: the request's own title and wording. Pure. */
export function workOrderDescription(w: Pick<WorkOrder, "title" | "description" | "externalRef" | "sourceLabel">): string {
  const text = [w.title, w.description].filter(Boolean).join(". ");
  // The platform's own reference travels with the work so it can be quoted back (the same wording the Guvnor tool uses).
  // Only a reference with letters in it (Fixflo's) is added; Dwellant's digits-only wording is unchanged.
  const withRef = text && /\D/.test(w.externalRef) ? `${text} (${w.sourceLabel} ref ${w.externalRef})` : text;
  return (withRef || `Request ${w.externalRef} from ${w.sourceLabel}`).slice(0, 1000);
}

// Session AUB: also the "Work orders" tab of the Mail workspace (embedded: the workspace carries the page title).
export default function WorkOrdersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [filter, setFilter] = useState<WorkOrderStatusFilter>("pending");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<WorkOrder[]>([]);
  const [counts, setCounts] = useState<{ pending: number; accepted: number; dismissed: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [notice, setNotice] = useState<{ text: string; link?: { to: string; label: string } } | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const t = setTimeout(() => {
      api.workOrders
        .list({ status: filter, search: search.trim() || undefined, limit: 100 })
        .then((r) => {
          if (!live) return;
          setRows(r.workOrders);
          setCounts(r.counts);
        })
        .catch((err) => live && setError(friendlyApiError(err, "Could not load the work orders.")))
        .finally(() => live && setLoading(false));
    }, search ? 250 : 0);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [filter, search, reloadKey]);

  function changed(text?: string, link?: { to: string; label: string }) {
    if (text) setNotice({ text, link });
    setReloadKey((k) => k + 1);
  }

  return (
    <div>
      {!embedded && <PageHeader
        title="Work orders"
        subtitle="Requests a managing agent's property platform (Dwellant, Fixflo) has assigned to ELE, read from the emails and matched to the site. Raise each one as a call-out or a repair in one click, or dismiss it."
        className="mb-4"
      />}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              filter === f.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
            }`}
          >
            {f.label}
            {counts && f.value !== "all" ? ` (${counts[f.value]})` : ""}
          </button>
        ))}
        <input
          type="search"
          aria-label="Search work orders"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, request number or building..."
          className={`ml-auto min-w-56 ${FIELD}`}
        />
      </div>

      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice.text}
          {notice.link && (
            <>
              {" "}
              <Link to={notice.link.to} className="text-ele-green underline-offset-2 hover:underline">
                {notice.link.label}
              </Link>
            </>
          )}
        </p>
      )}

      {loading ? (
        <Loader label="Loading the work orders..." rows={5} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={`mt-3 ${LINK_BTN}`}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          message={filter === "pending" ? "Nothing waiting" : "No work orders here"}
          hint={filter === "pending" ? "When a managing agent's platform assigns ELE a request, it appears here ready to raise." : "Nothing matches this view."}
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((w) => (
            <li key={w.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface">
              <button
                type="button"
                aria-expanded={openId === w.id}
                onClick={() => setOpenId((id) => (id === w.id ? null : w.id))}
                className="flex w-full flex-wrap items-start justify-between gap-3 px-4 py-3 text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ele-strong [overflow-wrap:anywhere]">{w.title}</p>
                  <p className="text-xs text-ele-body">
                    {w.sourceLabel} request <span className="text-ele-blue">{w.externalRef}</span>
                    {w.site ? ` at ${w.site.name}` : w.platformSiteName ? `, "${w.platformSiteName}" (site not chosen yet)` : ""}
                    {w.managedBy ? `, for ${w.managedBy}` : ""}
                  </p>
                  <p className="text-xs text-ele-body">
                    {w.latestEventLabel}, {when(w.latestEventAt)}
                  </p>
                </div>
                <StatusPill domain="workOrder" status={w.status} />
              </button>
              {openId === w.id && <WorkOrderPanel workOrder={w} onChanged={changed} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What the platform stated beyond the title. The authorisation limit is information for the office only (never on a customer document or an engineer screen). */
function PlatformDetails({ workOrder: w }: { workOrder: WorkOrder }) {
  const rows: Array<[string, string]> = [];
  if (w.jobType) rows.push(["Job type", w.jobType]);
  if (w.platformAddress) rows.push(["Address", w.platformAddress]);
  if (w.dueBy) rows.push(["Works due by", w.dueBy.split("-").reverse().join("/")]);
  if (w.priority) rows.push(["Priority", w.priority]);
  if (w.authorisationLimitPence != null) rows.push(["Works authorisation limit", `£${(w.authorisationLimitPence / 100).toFixed(2)} (for the office only: check any quote or invoice against it)`]);
  if (rows.length === 0 && !w.respondInPlatform) return null;
  return (
    <div className="mb-3">
      {rows.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-ele-body">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-xs uppercase tracking-wide">{k}</dt>
              <dd className="[overflow-wrap:anywhere]">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {w.respondInPlatform && (
        <p className="mt-2 text-ele-strong">
          {w.sourceLabel} sends from a no-reply address, so nobody reads an email reply. Respond in {w.sourceLabel} itself
          {w.platformInstance ? (
            <>
              :{" "}
              <a href={`https://${w.platformInstance}`} target="_blank" rel="noreferrer" className="text-ele-blue hover:underline">
                {w.platformInstance}
              </a>
            </>
          ) : (
            "."
          )}
        </p>
      )}
    </div>
  );
}

function WorkOrderPanel({ workOrder, onChanged }: { workOrder: WorkOrder; onChanged: (text?: string, link?: { to: string; label: string }) => void }) {
  const [detail, setDetail] = useState<WorkOrderDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [liftId, setLiftId] = useState("");
  const [text, setText] = useState(workOrderDescription(workOrder));
  const [dismissReason, setDismissReason] = useState("");
  // The site picker.
  const [siteQuery, setSiteQuery] = useState("");
  const [siteHits, setSiteHits] = useState<SitePrefill[]>([]);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    let live = true;
    api.workOrders
      .get(workOrder.id)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        if (d.lifts.length === 1) setLiftId(d.lifts[0]!.id);
      })
      .catch((err) => live && setLoadError(friendlyApiError(err, "Could not load this work order.")));
    return () => {
      live = false;
    };
  }, [workOrder.id, workOrder.site?.id]);

  useEffect(() => {
    const q = siteQuery.trim();
    if (q.length < 2) {
      setSiteHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      api.quotePrefill
        .sites(q)
        .then((hits) => live && setSiteHits(hits.slice(0, 8)))
        .catch(() => live && setSiteHits([]));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [siteQuery]);

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(friendlyApiError(err, "That could not be done."));
    } finally {
      setBusy(null);
    }
  }

  const pending = workOrder.status === "pending";
  const lifts = detail?.lifts ?? [];

  return (
    <div className="border-t border-ele-hairline px-4 py-3 text-sm">
      <PlatformDetails workOrder={workOrder} />
      {workOrder.description && <p className="mb-3 whitespace-pre-line text-ele-body [overflow-wrap:anywhere]">{workOrder.description}</p>}

      {workOrder.callOut && (
        <p className="mb-3 text-ele-strong">
          Raised as a call-out{workOrder.callOut.ref ? ` (${workOrder.callOut.ref})` : ""}.{" "}
          <Link to="/office/callouts" className="text-ele-green hover:underline">
            Call-outs
          </Link>
        </p>
      )}
      {workOrder.job && (
        <p className="mb-3 text-ele-strong">
          Raised as a repair{workOrder.job.ref ? ` (${workOrder.job.ref})` : ""}.{" "}
          <Link to="/office/scheduling" className="text-ele-green hover:underline">
            Scheduling
          </Link>
        </p>
      )}
      {workOrder.status === "dismissed" && (
        <p className="mb-3 text-ele-body">
          Dismissed {when(workOrder.dismissedAt)}
          {workOrder.dismissReason ? `: ${workOrder.dismissReason}` : "."}
        </p>
      )}

      {loadError && (
        <p role="alert" className="mb-2 text-ele-danger">
          {loadError}
        </p>
      )}

      {pending && (
        <div className="space-y-4">
          {/* The site. */}
          <div>
            <p className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">Site</p>
            {workOrder.site ? (
              <p className="text-ele-strong">
                {workOrder.site.name}
                {workOrder.site.customerName ? ` (${workOrder.site.customerName})` : ""}
                <span className="ml-2 text-xs text-ele-body">{workOrder.siteMatch === "auto" ? "matched from the platform's name" : "chosen by the office"}</span>
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={() => void run("clear", async () => {
                    await api.workOrders.setSite(workOrder.id, null);
                    onChanged("Site cleared.");
                  })}
                  className={`ml-3 ${LINK_BTN}`}
                >
                  Change site
                </button>
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-ele-body">
                  The platform calls this building {workOrder.platformSiteName ? `"${workOrder.platformSiteName}"` : "nothing recognisable"}, which matches no site.
                  Choose the site:
                </p>
                <input type="search" aria-label="Find the site" value={siteQuery} onChange={(e) => setSiteQuery(e.target.value)} placeholder="Type the site's name..." className={`w-full max-w-md ${FIELD}`} />
                {workOrder.platformSiteName && (
                  <label className="flex items-center gap-1.5 text-ele-body">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    Remember &ldquo;{workOrder.platformSiteName}&rdquo; as a name for the site, so the next request matches by itself
                  </label>
                )}
                {siteHits.length > 0 && (
                  <ul className="max-w-md divide-y divide-ele-hairline rounded-ele border border-ele-hairline">
                    {siteHits.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          disabled={busy != null}
                          onClick={() => void run("site", async () => {
                            const r = await api.workOrders.setSite(workOrder.id, s.id, remember);
                            onChanged(r.rememberedAlias ? `Site set to ${s.name}, and "${r.rememberedAlias}" remembered as its name on the platform.` : `Site set to ${s.name}.`);
                          })}
                          className="w-full px-3 py-2 text-left hover:bg-ele-surface-soft"
                        >
                          <span className="text-ele-strong">{s.name}</span>
                          <span className="block text-xs text-ele-body">
                            {s.customerName}, {s.address}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Raise the work. */}
          {workOrder.site && (
            <div className="space-y-2">
              <p className="font-menu text-xs uppercase tracking-wide text-ele-body">Raise the work</p>
              {lifts.length > 0 ? (
                <label className="flex max-w-md flex-col gap-1 text-ele-body">
                  Lift
                  <select value={liftId} onChange={(e) => setLiftId(e.target.value)} className={FIELD}>
                    {lifts.length > 1 && <option value="">Choose the lift...</option>}
                    {lifts.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                detail && <p className="text-ele-body">This site has no lifts on the register, so only a one-off repair at the site can be raised.</p>
              )}
              <label className="flex flex-col gap-1 text-ele-body">
                What needs doing
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} className={FIELD} />
              </label>
              <div className="flex flex-wrap gap-3">
                {lifts.length > 0 && (
                  <Button variant="secondary" size="sm" disabled={busy != null || !liftId || text.trim().length < 3} onClick={() => void run("callout", async () => {
                      const c = await api.callouts.create({ assetId: liftId, reportedAt: workOrder.receivedAt, faultDescription: text.trim(), workOrderProposalId: workOrder.id });
                      onChanged(`Call-out logged${c.invoiced ? ", with a draft invoice in Xero" : ""}.`, { to: "/office/callouts", label: "Open Call-outs" });
                    })}>
                    {busy === "callout" ? "Raising..." : "Raise call-out"}
                  </Button>
                )}
                <Button variant="secondary" size="sm" disabled={busy != null || (lifts.length > 0 && !liftId) || text.trim().length < 3} onClick={() => void run("repair", async () => {
                    const r = await api.schedule.plannedRepair(
                      liftId
                        ? { assetId: liftId, description: text.trim(), workOrderProposalId: workOrder.id }
                        : { customerId: workOrder.site!.customerId, siteLabel: workOrder.site!.name, description: text.trim(), workOrderProposalId: workOrder.id },
                    );
                    onChanged(r.reused ? "The lift already had an open repair, so the work order was linked to it." : "Repair raised.", { to: "/office/scheduling", label: "Open Scheduling" });
                  })}>
                  {busy === "repair" ? "Raising..." : lifts.length > 0 ? "Raise repair" : "Raise one-off repair"}
                </Button>
              </div>
              <p className="text-xs text-ele-body">The call-out&apos;s reported time is when the request reached us ({when(workOrder.receivedAt)}). Nothing is sent to anyone.</p>
            </div>
          )}

          {/* Dismiss. */}
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-64 flex-1 flex-col gap-1 text-ele-body">
              No work needed? Say why (optional)
              <input type="text" value={dismissReason} maxLength={500} onChange={(e) => setDismissReason(e.target.value)} placeholder="Raised after the visit" className={FIELD} />
            </label>
            <ConfirmButton
              onConfirm={() => void run("dismiss", async () => {
                await api.workOrders.dismiss(workOrder.id, dismissReason.trim() || undefined);
                onChanged("Work order dismissed. It can be restored from the Dismissed list.");
              })}
              label="Dismiss"
              confirmLabel="Dismiss it? Press again"
              busy={busy === "dismiss"}
              busyLabel="Dismissing..."
              disabled={busy != null}
              className={LINK_BTN}
            />
          </div>
        </div>
      )}

      {workOrder.status === "dismissed" && (
        <button
          type="button"
          disabled={busy != null}
          onClick={() => void run("restore", async () => {
            await api.workOrders.restore(workOrder.id);
            onChanged("Work order restored to the waiting list.");
          })}
          className={LINK_BTN}
        >
          {busy === "restore" ? "Restoring..." : "Restore"}
        </button>
      )}

      {detail && detail.messages.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">Emails about this request</p>
          <ul className="space-y-1">
            {detail.messages.map((m) => (
              <li key={m.id} className="text-xs text-ele-body [overflow-wrap:anywhere]">
                {when(m.receivedAt)}: {m.eventLabel ?? "Email"}
                {m.needsReply ? " (a person wrote: it is in the Enquiries queue for a reply)" : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {workOrder.status !== "accepted" && (
        <div className="mt-4 border-t border-ele-hairline pt-3">
          <ConfirmButton
            onConfirm={() => void run("delete", async () => {
              await api.workOrders.remove(workOrder.id);
              onChanged("Work order deleted. Its emails stay on record.");
            })}
            label="Delete work order"
            confirmLabel="Delete it for good? Press again"
            busy={busy === "delete"}
            busyLabel="Deleting..."
            disabled={busy != null}
            className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          />
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-ele-strong">
          {error}
        </p>
      )}
    </div>
  );
}
